import { Engine } from '@babylonjs/core/Engines/engine';
import { type Scene } from '@babylonjs/core/scene';
import { EngineContext } from './engineContext';

export class ViewportCore {
  /** Time before viewport gets idle after last change. */
  renderLoopIdleTime: number;

  /** Canvas */
  private readonly _canvas: HTMLCanvasElement;
  /** Resize observer */
  private readonly _resizeObserver: ResizeObserver;
  /** Babylon engine. */
  private readonly _engine: Engine;
  /** Babylon scene. */
  private readonly _scene: Scene;
  /** Graphics engine context. */
  private readonly _engineContext: EngineContext;
  /** Last time a change was received. */
  private _renderLoopLastMark!: number;

  /**
   * Creates a new viewport core.
   * @param canvas - canvas to use for rendering.
   */
  constructor(canvas: HTMLCanvasElement) {
    this._canvas = canvas;

    this._engine = new Engine(canvas, true);
    this._engineContext = new EngineContext(this._engine);

    this._scene = this._engineContext.createScene();

    // resize observer
    this._resizeObserver = new ResizeObserver(() => {
      this.resizeCallback();
    });
    this._resizeObserver.observe(canvas);

    this.renderLoopIdleTime = 5000;
  }

  /**
   * Returns the graphics engine.
   */
  get canvas(): HTMLCanvasElement {
    return this._canvas;
  }

  /**
   * Returns the graphics engine.
   */
  get engine(): Engine {
    return this._engine;
  }

  /**
   * Returns the graphics scene.
   */
  get scene(): Scene {
    return this._scene;
  }

  /**
   * Returns the graphics engine context.
   */
  get engineContext(): EngineContext {
    return this._engineContext;
  }

  /**
   * Notify changes to the viewport.
   */
  markDirty() {
    this._renderLoopLastMark = Date.now() + this.renderLoopIdleTime;
  }

  /**
   * Run render loop.
   */
  runRenderLoop() {
    this._engine.runRenderLoop(() => {
      this._scene.render();
    });
  }

  /**
   * Run render loop.
   */
  runRenderLoopIdle() {
    const scene = this._scene;

    // Connect animations events to notify changes
    scene.onBeforeAnimationsObservable.add(data => {
      if (data.animatables.length) {
        this.markDirty();
      }
    });

    // Connect camera events to notify changes
    const camera = scene.activeCamera!;
    camera.onViewMatrixChangedObservable.add(() => {
      this.markDirty();
    });

    // Notify before run
    this.markDirty();

    // Run loop
    this._engine.runRenderLoop(() => {
      // Update active camera outside of scene render call
      const camera = scene.activeCamera!;
      camera.update();

      // Skip render when viewport is idle
      const idleTime = Date.now() - this._renderLoopLastMark;
      if (idleTime > 0) {
        // log.debug('Skip frame');
        return;
      }

      // Render without camera updates
      scene.render(false);
    });
  }

  /**
   * Callback called whenever an observed resize occurs.
   */
  private resizeCallback() {
    this._engine.resize();
    this.markDirty();
  }
}
